"use client";

import format from "date-fns/format";
import { Globe, Copy, Video, SquareArrowOutUpRight } from "lucide-react";
import { Select, SelectTrigger, SelectContent, SelectItem } from "@/components/new/select";
import Link from "next/link";
import { Button } from "@/components/new/button";
import EventTimer from "@/components/shared/event-timer";
import toast from "react-hot-toast";
import Image from "next/image";
import { cn, parseToDate, getTimeZones, convertEventTime } from "@/lib/utils";
import { useMemo, useState } from "react";
import { extractEventLink, TEvent } from "@/types/events";

interface EventDetailsProps {
	onclick?: (event: TEvent) => void;
	event: TEvent;
	componentType?: "vertical" | "horizontal";
}

const DateDisplay = ({ label, date, time }: { label: string; date: string; time: string }) => (
	<div className="flex items-center gap-5 text-[18px] font-semibold text-[#475467]">
		<div className="w-[92px]">{label}</div>
		<div className="flex items-center rounded-md border border-[#0056E8] bg-white px-2 text-center">
			<span className="w-[125px] border-r border-[#0056E8] py-1 pr-2">{date}</span>
			<span className="w-[100px] py-1 pl-2">{time}</span>
		</div>
	</div>
);

export const EventDetails = ({ event, componentType = "vertical", onclick }: EventDetailsProps) => {
	const [isExpanded, setIsExpanded] = useState<boolean>(false);
	const [selectedTimezone, setSelectedTimezone] = useState(Intl.DateTimeFormat().resolvedOptions().timeZone);
	const [timezoneSearchTerm, setTimezoneSearchTerm] = useState("");

	const eventStartDate = new Date(event.dateTime);
	const eventEndDate = event.endDateTime ? new Date(event.endDateTime) : undefined;

	const formattedDate = format(eventStartDate, "EEE, MMM d");
	const formattedTime = format(eventStartDate, "hh:mm a");
	const formattedEndDate = eventEndDate ? format(eventEndDate, "EEE, MMM d") : null;
	const formattedEndTime = eventEndDate ? format(eventEndDate, "hh:mm a") : null;

	const toggleExpanded = () => setIsExpanded((prev) => !prev);

	const handleTimezoneChange = (timezone: string) => {
		setSelectedTimezone(timezone);
		setTimezoneSearchTerm("");

		const updatedStartDate = convertEventTime(event.dateTime, timezone);
		event.dateTime = updatedStartDate.toISOString();

		if (event.endDateTime) {
			const updatedEndDate = convertEventTime(event.endDateTime, timezone);
			event.endDateTime = updatedEndDate.toISOString();
		}
	};

	const timezones = getTimeZones();

	const filteredTimezones = useMemo(() => {
		if (!timezoneSearchTerm.trim()) return timezones;
		return timezones.filter((tz) =>
			tz.city.toLowerCase().includes(timezoneSearchTerm.toLowerCase()) ||
			tz.gmtOffset.toLowerCase().includes(timezoneSearchTerm.toLowerCase()) ||
			tz.label.toLowerCase().includes(timezoneSearchTerm.toLowerCase())
		);
	}, [timezoneSearchTerm, timezones]);


	return (
		<div className="space-y-4" onClick={onclick ? () => onclick(event) : undefined}>
			<h3 className="text-xl font-semibold">{event.title}</h3>
			<div className={componentType === "horizontal" ? "flex flex-col gap-4 xl:flex-row" : ""}>
				{event.coverImage && (
					<Image
						src={event.coverImage}
						alt={event.title || "Event"}
						className={cn("w-full object-contain", componentType === "horizontal" && "xl:w-1/2")}
						width={1080}
						height={1080}
					/>
				)}
				<div className="flex-1 space-y-4 pt-6">
					<div className="mt-6">
						<EventTimer
							dateTime={parseToDate(event.dateTime)}
							classNames={{
								tile: "gap-0 bg-[#F0F5FF]",
								timeCount: "text-5xl font-bold leading-[60px] text-[#0056E8]",
								timeUnit: "text-[8px] font-extrabold leading-[24px] text-gray-700",
							}}
						/>
					</div>
					<div
						className={cn(
							"flex gap-2",
							componentType === "vertical" && "items-center justify-between",
							componentType === "horizontal" && "flex-col items-stretch justify-center sm:flex-row"
						)}
					>
						<div
							className={cn(
								"flex flex-col gap-5 rounded-lg bg-[#F0F5FF] px-5 py-[10px]",
								componentType === "vertical" && "justify-between",
								componentType === "horizontal" && "items-center",
								!eventEndDate && "my-auto"
							)}
						>
							<DateDisplay label="Start Time" date={formattedDate} time={formattedTime} />
							{eventEndDate && (
								<DateDisplay label="End Time" date={formattedEndDate!} time={formattedEndTime!} />
							)}
						</div>

						<div
							className={cn(
								"flex flex-col items-start justify-center rounded-lg bg-[#F0F5FF] px-[20px] py-[10px]",
								componentType === "vertical" && "h-[110px]",
								componentType === "horizontal" && "sm:flex-col"
							)}
						>
							<Globe className="h-6 w-6 text-[#0056E8]" />

							<Select value={selectedTimezone} onValueChange={handleTimezoneChange} defaultValue={selectedTimezone}>
								<SelectTrigger
									className={cn(
										"flex flex-col items-start justify-center cursor-pointer border-none h-max shadow-none p-0 mx-0 mt-1 text-[#475467] text-[16px] font-semibold focus:ring-0 focus:outline-none focus:border-none"
									)}
									hideChevron={true}
								>
									<span>
										{filteredTimezones.find((tz) => tz.value === selectedTimezone)?.gmtOffset || "GMT"}
									</span>
									<div className="max-w-[80px]">
										<span className="block truncate overflow-hidden">
											{filteredTimezones.find((tz) => tz.value === selectedTimezone)?.city || selectedTimezone}
										</span>
									</div>
								</SelectTrigger>
								<SelectContent className="mt-2 max-h-60 w-64 rounded-md shadow-lg border border-gray-300 bg-white overflow-y-auto">
									<div className="px-3 py-2 border-b border-gray-200">
										<input
											type="text"
											placeholder="Search for a timezone"
											className="w-full rounded-md border px-2 py-1 text-sm text-gray-700 focus:outline-none"
											onChange={(e) => setTimezoneSearchTerm(e.target.value)}
											onClick={(e) => e.stopPropagation()}
										/>
									</div>
									{filteredTimezones.map((timezone) => (
										<SelectItem
											key={timezone.value}
											value={timezone.value}
											className="px-3 py-2 hover:bg-gray-100 cursor-pointer"
										>
											<div className="flex flex-col">
												<span className="text-sm text-gray-500">{timezone.label}</span>
											</div>
										</SelectItem>
									))}
								</SelectContent>
							</Select>
						</div>
					</div>

					{event.link && (
						<div
							className={cn(
								"flex items-center justify-between gap-4 rounded-md bg-blue-50 p-3",
								componentType === "horizontal" && "flex-row"
							)}
						>
							<Link
								href={event.link}
								target="_blank"
								rel="noopener noreferrer nofollow"
								className={cn(
									"flex gap-[5px] rounded-[4px] border border-[#0056E8] bg-white py-[5px]",
									componentType === "vertical" && "px-[10px]",
									componentType === "horizontal" && "px-[5px]"
								)}
							>
								<Video
									size={componentType === "vertical" ? 24.5 : 18}
									className="my-auto rounded-md bg-[#4087FC] fill-white px-[2px] py-[1px] text-white"
								/>
								<span
									className={cn(
										"font-semibold text-[#0056E8]",
										componentType === "vertical" ? "text-[18px]" : "text-[14px]"
									)}
								>
									Event Link
								</span>
							</Link>
							<Link
								href={extractEventLink(event)}
								target="_blank"
								rel="noopener noreferrer nofollow"
								className="block w-max max-w-[300px] truncate text-blue-600 hover:underline"
							>
								{extractEventLink(event)}
							</Link>
							<div className="flex gap-2">
								<Button
									variant={"ghost"}
									className="p-0"
									onClick={() => {
										if (navigator.clipboard) {
											navigator.clipboard.writeText(extractEventLink(event) || "#");
											toast.success("Link copied to clipboard");
										} else {
											toast.error("Clipboard API not supported");
										}
									}}
								>
									<Copy className="h-[33px] w-[33px] rounded-md border border-[#0056E8] bg-white p-[6px] text-[#0056E8]" />
								</Button>
								<Button variant={"ghost"} className="p-0">
									<Link
										href={extractEventLink(event) || "#"}
										target="_blank"
										rel="noopener noreferrer nofollow"
										className="block w-full truncate text-blue-600 hover:underline"
									>
										<SquareArrowOutUpRight className="h-[33px] w-[33px] rounded-md border border-[#0056E8] bg-white p-[6px] text-[#0056E8]" />
									</Link>
								</Button>
							</div>
						</div>
					)}
				</div>
			</div>
			<div className="text-gray-600">
				{isExpanded ? (
					<p
						className="whitespace-pre-line"
						dangerouslySetInnerHTML={{ __html: event.description || "" }}
					/>
				) : (
					<div className="line-clamp-2">
						<p
							className="whitespace-pre-line"
							dangerouslySetInnerHTML={{ __html: event.description || "" }}
						/>
					</div>
				)}
				{event.description && event.description.length > 0 && (
					<button type="button" className="mt-2 text-blue-500 underline" onClick={toggleExpanded}>
						{isExpanded ? "See less" : "See more"}
					</button>
				)}
			</div>
		</div>
	);
};
